import React, { FunctionComponent } from 'react'
import { Row, Col } from 'react-grid-system'
import { useTranslation } from 'react-i18next'

import { MaAncillaryOrderItem } from './MaAncillaryOrderItem'
import { MaOrderItem, OrderItemType } from './MaOrderItem'
import { StyledMaOrderItemDetailHeader } from './MaOrderItems.styles'
import { ClinTheme } from '../../ClinTheme'
import { ClinCard } from '../../components/ClinCard'
import { ClinText } from '../../components/ClinText'
import { isProductTemperatureTracker } from '../../pages/OpaCheckout/OpaCheckout.models'
import {
  HoldDto,
  OrderLineDto,
  OrderTrackingInfoDto,
  UploadedDocumentDto
} from '../../types/swaggerTypes'

interface IMaOrderItemsProps {
  /** The order id */
  orderId: string
  /** Order items */
  orderLines: OrderLineDto[]
  /** Program sponsor */
  programSponsor: string | undefined
  /** Currency code*/
  currencyCode: string
  /** Order item type */
  itemType: OrderItemType
  /** Hide/show file upload field */
  isFileUploadEnabled?: boolean
  /** Displays program details of each item */
  isShowingProductDetails?: boolean
  /** The order status */
  orderStatus?: string
  /** Display any tracking info */
  trackingInfo?: OrderTrackingInfoDto
  /** Called when a file has been uploaded */
  handleFileUploadedForOrderAndHold?: (
    orderId: string,
    orderLine: OrderLineDto,
    hold: HoldDto,
    document: UploadedDocumentDto
  ) => void
}

export const MaOrderItems: FunctionComponent<IMaOrderItemsProps> = ({
  orderId,
  orderLines,
  programSponsor,
  currencyCode,
  itemType,
  isFileUploadEnabled = false,
  isShowingProductDetails = true,
  handleFileUploadedForOrderAndHold
}) => {
  const { t } = useTranslation()

  const filteredOrderLines = orderLines.filter(
    (orderLine) => !isProductTemperatureTracker(orderLine.skuCatalogItem?.item)
  )

  return orderLines.length > 0 ? (
    <ClinCard removePadding={true}>
      <StyledMaOrderItemDetailHeader>
        <Row className="order-items-header">
          <Col sm={12} md={6}>
            <ClinText
              fontSize={ClinTheme.fontSizes[1]}
              fontWeight={ClinTheme.fontWeights.medium}
              color={ClinTheme.colors.primary}
            >
              {t('ma_order_detail:order_items.table_header_product')}
            </ClinText>
          </Col>
          <Col sm={12} md={2}>
            <ClinText
              fontSize={ClinTheme.fontSizes[1]}
              fontWeight={ClinTheme.fontWeights.medium}
              color={ClinTheme.colors.primary}
              textAlign="right"
            >
              {t('ma_order_detail:order_items.table_header_product_code')}
            </ClinText>
          </Col>
          <Col sm={12} md={2}>
            <ClinText
              fontSize={ClinTheme.fontSizes[1]}
              fontWeight={ClinTheme.fontWeights.medium}
              color={ClinTheme.colors.primary}
              textAlign="right"
            >
              {itemType === OrderItemType.ReassignedItem
                ? t('glossary:units')
                : t('glossary:quantity')}
            </ClinText>
          </Col>
          <Col sm={12} md={2}>
            <ClinText
              fontSize={ClinTheme.fontSizes[1]}
              fontWeight={ClinTheme.fontWeights.medium}
              color={ClinTheme.colors.primary}
              textAlign="right"
            >
              {t('glossary:amount')}
            </ClinText>
          </Col>
        </Row>
      </StyledMaOrderItemDetailHeader>

      {filteredOrderLines.map((orderLine, index) => {
        const hasBorder =
          filteredOrderLines.length > 1 && index < filteredOrderLines.length - 1
        return orderLine.medicationFlag === 'Y' ? (
          <MaOrderItem
            key={orderLine.itemId}
            orderId={orderId}
            orderLine={orderLine}
            sponsor={programSponsor}
            currencyCode={currencyCode}
            itemType={itemType}
            hasBorder={hasBorder}
            isFileUploadEnabled={isFileUploadEnabled}
            isShowingProductDetails={isShowingProductDetails}
            handleFileUploadedForOrderAndHold={
              handleFileUploadedForOrderAndHold
            }
          />
        ) : (
          <MaAncillaryOrderItem
            key={orderLine.itemId}
            orderLine={orderLine}
            currencyCode={currencyCode}
            hasBorder={hasBorder}
            isShowingProductDetails={isShowingProductDetails}
          />
        )
      })}
    </ClinCard>
  ) : (
    <Row>
      <Col>
        <ClinText fontSize={ClinTheme.fontSizes[3]}>
          {t('ma_order_detail:no_products_required')}
        </ClinText>
      </Col>
    </Row>
  )
}
